import { useRef, useState } from "react"
import type { ReactNode } from "react"
import {
  Button,
  Checkbox,
  DateInput,
  FormField,
  Select,
  Text,
  TextField,
  Textarea,
} from "@smooth/design-system-components"
import { counterpartFieldLabel } from "../../components/ContactLogPanel"
import {
  CONTACT_CHANNEL_LABELS,
  CONTACT_COUNTERPART_OPTIONS,
  CONTACT_DETAIL_OPTIONS,
  CONTACT_RESULT_OPTIONS,
  Panel,
  formatYen,
} from "../../components/shared"
import { PROMISE_SOURCE_LABELS } from "../caseFacts"
import { toJstIso } from "../types"
import type {
  Scenario,
  ScenarioActivePromise,
  ScenarioContactLog,
  ScenarioContract,
  ScenarioInstallment,
  ScenarioMemo,
  ScenarioPromiseHistoryEntry,
} from "../types"
import { InstallmentPlanForm } from "./InstallmentPlanForm"
import { ReadOnlyField, TextButton } from "./parts"

// 検証ケースの約束・交渉記録・メモ・契約（分割予定）を行の追加・削除で編集する欄。
// 項目名・選択肢は督促ケース詳細（支払約束・連絡を記録・メモ欄・支払いスケジュール）にそろえる。
// AI にも評価にも効かない項目（約束履歴の登録元・交渉記録のチャンネル／連絡回数に数えるか）は欄を出さず、
// 読み込んだ値のままでよい項目（有効な約束の登録元・契約の分割回数／総支払額）は表示だけにする。
// 分割の回・入金日は AI 入力（第N回・過去の入金）に出るので、分割ごとの「詳しい設定」から変えられるようにする。
// 契約の物件名・残回数・残高は AI 入力（契約の見出し・残支払い・残債）に出るので、「詳しい設定」から直接変えられるようにする
// （契約を見分ける名前と、分割予定に書かない回を作るため）。
// どちらも保存済みの値は行に残したまま保存し、新しく足す行には既定値を入れる
//
// 欄の見出しは短く（「約定日」等）し、どの行の欄かを含む一意な名前（「約定日 契約1 分割1」等）は aria-label に持たせる
// （行の見出しに「契約1 分割1」があるので、見出しにも繰り返すと長く折れて欄の上端がずれる）。
// 欄の列数は「中身の幅が 480px 以上なら2列、それ未満は1列」の1つに決める（日付欄が切れないように、画面幅ではなく中身の幅で判定する）

/** 欄を並べる格子。親に @container が要る（RecordRow・EditorBody が持つ） */
export const FIELD_GRID = "grid gap-4 @[480px]:grid-cols-2"

/** 見出しは短いまま、読み上げ・テスト用の一意な名前を aria-label に持たせる */
const named = (name: string, where: string) => ({ "aria-label": `${name} ${where}` })

/** 上限（サーバーの検証と揃える） */
export const RECORD_LIMITS = {
  promiseHistory: 10,
  contactLogs: 50,
  operatorMemos: 20,
  contracts: 10,
  installments: 60,
} as const

/** 時差（Z・+09:00 等）の付いた ISO8601 */
const WITH_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i

/**
 * datetime-local の値（日本時間の壁時計）と API の ISO8601（+09:00）を行き来する。
 * +09:00 以外（UTC の Z 等）で保存された日時も日本時間に直してから出す（先頭を切るだけだと時差の分ずれる）
 */
export const toLocalInput = (iso: string | null | undefined) => {
  if (!iso) return ""
  const time = Date.parse(iso)
  if (!WITH_OFFSET.test(iso) || Number.isNaN(time)) return iso.slice(0, 16)
  return toJstIso(time).slice(0, 16)
}
export const fromLocalInput = (value: string) => (value ? `${value}:00+09:00` : "")

/** 数値入力。空欄は null */
const optionalNumber = (value: string) => (value.trim() === "" ? null : Number(value))

const replaceAt = <T,>(items: T[], index: number, patch: Partial<T>) =>
  items.map((item, i) => (i === index ? { ...item, ...patch } : item))
const removeAt = <T,>(items: T[], index: number) => items.filter((_, i) => i !== index)

const CHANNEL_OPTIONS = (["phone", "sms", "email", "in_person", "other"] as const).map((code) => ({
  value: code,
  label: CONTACT_CHANNEL_LABELS[code] ?? code,
}))
const DIRECTION_OPTIONS = [
  { value: "outbound", label: "発信（当社から）" },
  { value: "inbound", label: "着信（お客様から）" },
]
/** datetime（+09:00）を「YYYY/MM/DD HH:mm」で出す（日本時間の壁時計のまま） */
const formatLocalDateTime = (iso: string | null | undefined) =>
  iso ? toLocalInput(iso).replace("T", " ").replaceAll("-", "/") : "—"
/** 日付（YYYY-MM-DD）を「YYYY/MM/DD」で出す */
const formatLocalDate = (date: string | null | undefined) =>
  date ? date.replaceAll("-", "/") : "—"
/** 選択肢の先頭に「未選択」を足す */
const withNone = (options: { code: string; label: string }[]) => [
  { value: "", label: "未選択" },
  ...options.map(({ code, label }) => ({ value: code, label })),
]

/** 行1件の枠。見出しと削除ボタンを持つ。削除は督促詳細のメモ・交渉記録と同じ文字だけのボタンにする */
function RecordRow({
  title,
  onRemove,
  busy,
  aside,
  children,
}: {
  title: string
  onRemove: () => void
  busy: boolean
  /** 見出しの横に添える表示（分割の「第2回」など） */
  aside?: ReactNode
  children: ReactNode
}) {
  return (
    <li className="@container flex flex-col gap-3 border-b border-border-default py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex min-w-0 items-baseline gap-3">
          <Text as="span" size="sm" weight="bold">
            {title}
          </Text>
          {aside}
        </span>
        <TextButton aria-label={`${title}を削除`} disabled={busy} onClick={onRemove}>
          削除
        </TextButton>
      </div>
      {children}
    </li>
  )
}

function PanelError({ message }: { message?: string }) {
  if (!message) return null
  return (
    <p role="alert" className="mb-2 text-[1.2rem] text-text-error">
      {message}
    </p>
  )
}

type EditorProps = {
  scenario: Scenario
  busy: boolean
  error?: string
  onChange: (patch: Partial<Scenario>) => void
}

/** 支払約束（有効な約束と約束の履歴）。督促画面の支払約束パネルに当たる */
export function PromiseEditor({ scenario, busy, error, onChange }: EditorProps) {
  const active = scenario.activePromise ?? null
  const history = scenario.promiseHistory ?? []
  const editActive = (patch: Partial<ScenarioActivePromise>) =>
    onChange({ activePromise: { ...(active as ScenarioActivePromise), ...patch } })
  const editHistory = (index: number, patch: Partial<ScenarioPromiseHistoryEntry>) =>
    onChange({ promiseHistory: replaceAt(history, index, patch) })

  return (
    <Panel title="支払約束（Promise）">
      <PanelError message={error} />
      <div className="@container flex flex-col gap-3">
        <Checkbox
          label="有効な約束あり（期日は基準日時以降）"
          checked={!!active}
          disabled={busy}
          onChange={(e) =>
            onChange({
              activePromise: e.target.checked
                ? { scheduledOn: scenario.asOf, amount: null, source: "dunning_payment_promise" }
                : null,
            })
          }
        />
        {active && (
          <div className={FIELD_GRID}>
            <FormField label="約束期日（日本時間）">
              <TextField
                type="datetime-local"
                value={toLocalInput(active.scheduledOn)}
                onChange={(e) => editActive({ scheduledOn: fromLocalInput(e.target.value) })}
              />
            </FormField>
            <FormField label="金額（任意・全額なら空欄）">
              <TextField
                type="number"
                min={1}
                value={active.amount ?? ""}
                onChange={(e) => editActive({ amount: optionalNumber(e.target.value) })}
              />
            </FormField>
            <ReadOnlyField label="登録元">
              {active.source ? PROMISE_SOURCE_LABELS[active.source] : "不明"}
            </ReadOnlyField>
          </div>
        )}
      </div>

      <h4 className="mt-5 text-[1.2rem] font-semibold text-black">
        約束履歴（{history.length}件・古い順に AI へ渡します）
      </h4>
      <ul className="flex flex-col">
        {history.map((promise, index) => (
          <RecordRow
            key={index}
            title={`約束履歴 ${index + 1}`}
            busy={busy}
            onRemove={() => onChange({ promiseHistory: removeAt(history, index) })}
          >
            <div className={FIELD_GRID}>
              <FormField label="約束した日時">
                <TextField
                  {...named("約束した日時", `${index + 1}`)}
                  type="datetime-local"
                  value={toLocalInput(promise.promisedAt)}
                  onChange={(e) =>
                    editHistory(index, { promisedAt: fromLocalInput(e.target.value) })
                  }
                />
              </FormField>
              <FormField label="約束期日">
                <TextField
                  {...named("約束期日", `${index + 1}`)}
                  type="datetime-local"
                  value={toLocalInput(promise.scheduledOn)}
                  onChange={(e) =>
                    editHistory(index, { scheduledOn: fromLocalInput(e.target.value) })
                  }
                />
              </FormField>
              <FormField label="金額（空欄は不明）">
                <TextField
                  aria-label={`金額 ${index + 1}（空欄は不明）`}
                  type="number"
                  min={1}
                  value={promise.amount ?? ""}
                  onChange={(e) => editHistory(index, { amount: optionalNumber(e.target.value) })}
                />
              </FormField>
              <FormField label="結果" hint="未払いで期日を過ぎていれば不履行として渡ります。">
                <Select
                  {...named("結果", `${index + 1}`)}
                  value={promise.fulfilled ? "fulfilled" : "unpaid"}
                  onChange={(e) =>
                    editHistory(index, { fulfilled: e.target.value === "fulfilled" })
                  }
                  options={[
                    { value: "unpaid", label: "未払い（不履行・期日前）" },
                    { value: "fulfilled", label: "支払済み" },
                  ]}
                />
              </FormField>
            </div>
          </RecordRow>
        ))}
      </ul>
      <div className="pt-3">
        <Button
          variant="outline"
          disabled={busy || history.length >= RECORD_LIMITS.promiseHistory}
          onClick={() =>
            onChange({
              promiseHistory: [
                ...history,
                {
                  promisedAt: scenario.asOf,
                  scheduledOn: scenario.asOf,
                  amount: null,
                  fulfilled: false,
                  source: "dunning_payment_promise",
                },
              ],
            })
          }
        >
          約束履歴を追加
        </Button>
      </div>
    </Panel>
  )
}

/** 交渉記録（LINE 以外）。督促画面の「連絡を記録」と同じ項目 */
export function ContactLogEditor({ scenario, busy, error, onChange }: EditorProps) {
  const logs = scenario.contactLogs ?? []
  const edit = (index: number, patch: Partial<ScenarioContactLog>) =>
    onChange({ contactLogs: replaceAt(logs, index, patch) })

  return (
    <Panel title="交渉記録">
      <PanelError message={error} />
      <Text size="sm">
        LINE以外の連絡（電話・SMS・メール等）を入れます。AIには新しい順に10件まで渡します。LINEのやり取りは督促チャットに入れてください。
      </Text>
      <ul className="flex flex-col">
        {logs.map((log, index) => (
          <RecordRow
            key={index}
            title={`交渉記録 ${index + 1}`}
            busy={busy}
            onRemove={() => onChange({ contactLogs: removeAt(logs, index) })}
          >
            <div className={FIELD_GRID}>
              <FormField label="連絡日時">
                <TextField
                  {...named("連絡日時", `${index + 1}`)}
                  type="datetime-local"
                  value={toLocalInput(log.contactedAt)}
                  onChange={(e) => edit(index, { contactedAt: fromLocalInput(e.target.value) })}
                />
              </FormField>
              <FormField label="連絡手段">
                <Select
                  {...named("連絡手段", `${index + 1}`)}
                  value={log.channel}
                  onChange={(e) => {
                    const channel = e.target.value as ScenarioContactLog["channel"]
                    // チャンネル・内容は電話専用。電話以外へ切り替えたらクリアする
                    edit(index, {
                      channel,
                      ...(channel === "phone" ? {} : { phoneLine: null, contactDetail: null }),
                    })
                  }}
                  options={CHANNEL_OPTIONS}
                />
              </FormField>
              <FormField label="方向">
                <Select
                  {...named("方向", `${index + 1}`)}
                  value={log.direction}
                  onChange={(e) =>
                    edit(index, { direction: e.target.value as ScenarioContactLog["direction"] })
                  }
                  options={DIRECTION_OPTIONS}
                />
              </FormField>
              <FormField label={counterpartFieldLabel(log.channel)}>
                <Select
                  {...named(counterpartFieldLabel(log.channel), `${index + 1}`)}
                  value={log.counterpart ?? ""}
                  onChange={(e) =>
                    edit(index, {
                      counterpart: (e.target.value || null) as ScenarioContactLog["counterpart"],
                    })
                  }
                  options={withNone(CONTACT_COUNTERPART_OPTIONS)}
                />
              </FormField>
              <FormField label="接触">
                <Select
                  {...named("接触", `${index + 1}`)}
                  value={log.contactResult ?? ""}
                  onChange={(e) =>
                    edit(index, {
                      contactResult: (e.target.value ||
                        null) as ScenarioContactLog["contactResult"],
                    })
                  }
                  options={withNone(CONTACT_RESULT_OPTIONS)}
                />
              </FormField>
              {log.channel === "phone" && (
                <FormField label="内容">
                  <Select
                    {...named("内容", `${index + 1}`)}
                    value={log.contactDetail ?? ""}
                    onChange={(e) =>
                      edit(index, {
                        contactDetail: (e.target.value ||
                          null) as ScenarioContactLog["contactDetail"],
                      })
                    }
                    options={withNone(CONTACT_DETAIL_OPTIONS)}
                  />
                </FormField>
              )}
            </div>
            <FormField label="メモ（任意）">
              <Textarea
                aria-label={`メモ ${index + 1}（任意）`}
                rows={2}
                maxLength={5000}
                value={log.note ?? ""}
                placeholder="通話内容・経緯など"
                onChange={(e) => edit(index, { note: e.target.value || null })}
              />
            </FormField>
          </RecordRow>
        ))}
      </ul>
      <div className="pt-3">
        <Button
          variant="outline"
          disabled={busy || logs.length >= RECORD_LIMITS.contactLogs}
          onClick={() =>
            onChange({
              contactLogs: [
                ...logs,
                {
                  contactedAt: scenario.asOf,
                  channel: "phone",
                  direction: "outbound",
                  counterpart: null,
                  contactResult: null,
                  contactDetail: null,
                  phoneLine: null,
                  note: null,
                  countsAsContact: true,
                },
              ],
            })
          }
        >
          連絡を記録
        </Button>
      </div>
    </Panel>
  )
}

/**
 * メモ欄（オペレーターメモ）と事実メモ（古い形式）。事実メモの欄は、開いた時点で事実メモを持つ古いケースにだけ出す
 * （新しいケースはメモ欄に書く。入力中に空にしても欄は消さない）
 */
export function MemoEditor({ scenario, busy, error, onChange }: EditorProps) {
  const memos = scenario.operatorMemos ?? []
  const [showFacts] = useState(() => scenario.facts.trim() !== "")
  return (
    <Panel title="メモ（タイトル・本文・日時が AI に渡ります）">
      <PanelError message={error} />
      <Text size="sm">AIにはオペレーターメモとして新しい順に5件まで渡します。</Text>
      <ul className="flex flex-col">
        {memos.map((memo, index) => (
          <RecordRow
            key={index}
            title={`メモ ${index + 1}`}
            busy={busy}
            onRemove={() => onChange({ operatorMemos: removeAt(memos, index) })}
          >
            <FormField label="記入日時">
              <TextField
                {...named("記入日時", `${index + 1}`)}
                type="datetime-local"
                value={toLocalInput(memo.createdAt)}
                onChange={(e) =>
                  onChange({
                    operatorMemos: replaceAt<ScenarioMemo>(memos, index, {
                      createdAt: fromLocalInput(e.target.value),
                    }),
                  })
                }
              />
            </FormField>
            <FormField label="タイトル（任意）">
              <TextField
                {...named("タイトル", `${index + 1}`)}
                maxLength={255}
                value={memo.title ?? ""}
                onChange={(e) =>
                  onChange({
                    operatorMemos: replaceAt<ScenarioMemo>(memos, index, { title: e.target.value }),
                  })
                }
              />
            </FormField>
            <FormField label="本文">
              <Textarea
                {...named("本文", `${index + 1}`)}
                rows={2}
                maxLength={5000}
                value={memo.body}
                onChange={(e) =>
                  onChange({
                    operatorMemos: replaceAt<ScenarioMemo>(memos, index, { body: e.target.value }),
                  })
                }
              />
            </FormField>
          </RecordRow>
        ))}
      </ul>
      <div className="pb-4 pt-3">
        <Button
          variant="outline"
          disabled={busy || memos.length >= RECORD_LIMITS.operatorMemos}
          onClick={() =>
            onChange({ operatorMemos: [...memos, { createdAt: scenario.asOf, body: "" }] })
          }
        >
          メモを追加
        </Button>
      </div>
      {showFacts && (
        <FormField
          label="事実メモ（基準日時のメモとして渡します）"
          hint="約束・電話の記録は支払約束・交渉記録の欄に入れてください。ここには送信済み依頼回数・未回答項目など、欄の無い事実だけを書きます。望ましい答えや生成指示は書かないでください。"
        >
          <Textarea
            rows={4}
            maxLength={10000}
            value={scenario.facts}
            onChange={(e) => onChange({ facts: e.target.value })}
          />
        </FormField>
      )}
    </Panel>
  )
}

/** 分割予定に足す行の回（その契約の最後の回の次。回の無い行ばかりなら行の数から） */
const nextInstallmentNumber = (rows: ScenarioInstallment[]) => {
  const numbered = rows.flatMap((row) => (row.numberOfPayment == null ? [] : [row.numberOfPayment]))
  // 回の上限（サーバの検証と同じ 1000）を超えて振らない
  return Math.min(numbered.length ? Math.max(...numbered) + 1 : rows.length + 1, 1000)
}

const emptyInstallment = (
  scenario: Scenario,
  rows: ScenarioInstallment[]
): ScenarioInstallment => ({
  numberOfPayment: nextInstallmentNumber(rows),
  scheduledOn: scenario.nextDueOn,
  paymentTotalAmount: 30_000,
  paidAmount: 0,
})

const unpaidOf = (rows: ScenarioInstallment[]) => {
  const unpaid = rows.filter((row) => row.paidAmount < row.paymentTotalAmount)
  return {
    count: unpaid.length,
    amount: unpaid.reduce((sum, row) => sum + row.paymentTotalAmount - row.paidAmount, 0),
  }
}

/**
 * 分割予定を置き換える。分割予定に書かない回（残回数・残高と分割予定の未払いとの差）を保ったまま、
 * 分割予定の未払いの回数・額の増減だけずらす（検証の下限＝分割予定の未払いは割らない）
 */
export const withInstallments = (
  contract: ScenarioContract,
  installments: ScenarioInstallment[]
): ScenarioContract => {
  const before = unpaidOf(contract.installments)
  const after = unpaidOf(installments)
  return {
    ...contract,
    installments,
    remainingPaymentCount: Math.max(
      after.count,
      contract.remainingPaymentCount + after.count - before.count
    ),
    remainingBalance: Math.max(
      after.amount,
      contract.remainingBalance + after.amount - before.amount
    ),
  }
}

/** 振替日程（振替日・結果日・振替請求）の要約。折りたたみの見出しに出す */
const transferSummary = (row: ScenarioInstallment) =>
  row.transferOn || row.resultOn || row.transferRequested
    ? [
        `振替：振替日 ${formatLocalDate(row.transferOn)}`,
        `結果日 ${formatLocalDate(row.resultOn)}`,
        row.transferRequested ? "振替請求あり" : "振替請求なし",
      ].join("・")
    : "振替：未設定（約定日の翌日に延滞確定）"

/**
 * 行ごとに振る key。契約には ID が無く、添字を key にすると削除で詰めたときに下の行が上の行の入力中の値
 * （分割の予定をまとめて作る欄）を引き継ぐので、追加した行に番号を振り、削除した行の番号を抜く
 */
function useRowKeys(length: number) {
  const next = useRef(0)
  const keys = useRef<number[]>([])
  while (keys.current.length < length) keys.current.push(next.current++)
  if (keys.current.length > length) keys.current = keys.current.slice(0, length)
  const remove = (index: number) => {
    keys.current = keys.current.filter((_, i) => i !== index)
  }
  return { keys: keys.current, remove }
}

/** 契約（分割予定）。督促画面の契約情報・支払いスケジュールに当たる */
export function ContractEditor({ scenario, busy, error, onChange }: EditorProps) {
  const contracts = scenario.contracts ?? []
  const rowKeys = useRowKeys(contracts.length)
  const editContract = (contractIndex: number, patch: Partial<ScenarioContract>) =>
    onChange({ contracts: replaceAt(contracts, contractIndex, patch) })
  const setInstallments = (contractIndex: number, installments: ScenarioInstallment[]) =>
    onChange({
      contracts: replaceAt(
        contracts,
        contractIndex,
        withInstallments(contracts[contractIndex], installments)
      ),
    })
  const editInstallment = (
    contractIndex: number,
    rowIndex: number,
    patch: Partial<ScenarioInstallment>
  ) =>
    setInstallments(
      contractIndex,
      replaceAt(contracts[contractIndex].installments, rowIndex, patch)
    )

  if (contracts.length === 0) {
    return (
      <Panel title="契約情報">
        <PanelError message={error} />
        <FormField
          label="次回約定日"
          hint="契約の分割予定を入れていないケースは、架空の契約1件（全12回・第2回が遅延分・第3回が次回30,000円）として AI に渡します。"
        >
          <DateInput
            value={scenario.nextDueOn}
            onChange={(e) => onChange({ nextDueOn: e.target.value })}
          />
        </FormField>
        <div className="pt-3">
          <InstallmentPlanForm
            scenario={scenario}
            contractIndex={0}
            busy={busy}
            onChange={onChange}
            defaultOpen
          />
        </div>
      </Panel>
    )
  }

  return (
    <Panel title="契約情報">
      <PanelError message={error} />
      <Text size="sm">
        遅延金額と次回約定日は分割予定から計算します（延滞の確定は振替請求ありなら結果日の13時、なしなら振替日の翌日、振替日が空なら約定日の翌日）。残回数・残高は分割予定に書かない回も含めた契約全体の値で、分割予定の未払いの増減に合わせて動きます。
      </Text>
      <ul className="flex flex-col">
        {contracts.map((contract, contractIndex) => (
          <RecordRow
            key={rowKeys.keys[contractIndex]}
            title={`契約${contractIndex + 1}`}
            busy={busy}
            onRemove={() => {
              rowKeys.remove(contractIndex)
              onChange({ contracts: removeAt(contracts, contractIndex) })
            }}
          >
            <div className={FIELD_GRID}>
              <ReadOnlyField label="分割回数" ariaLabel={`分割回数 契約${contractIndex + 1}`}>
                {contract.numberOfPayment == null ? "—" : `全${contract.numberOfPayment}回`}
              </ReadOnlyField>
              <ReadOnlyField label="総支払額" ariaLabel={`総支払額 契約${contractIndex + 1}`}>
                {formatYen(contract.totalPaymentAmount)}
              </ReadOnlyField>
              <ReadOnlyField label="残回数" ariaLabel={`残回数 契約${contractIndex + 1}`}>
                {`${contract.remainingPaymentCount}回`}
              </ReadOnlyField>
              <ReadOnlyField label="残高" ariaLabel={`残高 契約${contractIndex + 1}`}>
                {formatYen(contract.remainingBalance)}
              </ReadOnlyField>
              <ReadOnlyField label="請求停止" ariaLabel={`請求停止 契約${contractIndex + 1}`}>
                {contract.billingStopped ? "あり（引き落とし停止中）" : "なし"}
              </ReadOnlyField>
            </div>
            <details
              aria-label={`詳しい設定 契約${contractIndex + 1}`}
              className="rounded-md border border-border-default px-3 py-2"
            >
              <summary className="cursor-pointer text-caption text-text-default">
                詳しい設定（物件名・残回数・残高・請求停止）：
                {contract.propertyName || "物件名なし"}
              </summary>
              <Text size="sm">
                物件名は AI
                入力の契約の見出しに出ます。残回数・残高は分割予定に書かない回も含めた契約全体の値で、分割予定の未払いより大きくすると、書かない回があるケースになります。
              </Text>
              <div className="grid gap-4 pt-3 min-[600px]:grid-cols-3">
                <FormField label={`物件名 契約${contractIndex + 1}（任意）`}>
                  <TextField
                    maxLength={150}
                    value={contract.propertyName ?? ""}
                    onChange={(e) =>
                      editContract(contractIndex, { propertyName: e.target.value || null })
                    }
                  />
                </FormField>
                <FormField label={`残回数を変える 契約${contractIndex + 1}`}>
                  <TextField
                    type="number"
                    min={0}
                    value={contract.remainingPaymentCount}
                    onChange={(e) =>
                      editContract(contractIndex, { remainingPaymentCount: Number(e.target.value) })
                    }
                  />
                </FormField>
                <FormField label={`残高を変える 契約${contractIndex + 1}`}>
                  <TextField
                    type="number"
                    min={0}
                    value={contract.remainingBalance}
                    onChange={(e) =>
                      editContract(contractIndex, { remainingBalance: Number(e.target.value) })
                    }
                  />
                </FormField>
              </div>
              <div className="pt-3">
                <Checkbox
                  label={`請求停止あり 契約${contractIndex + 1}`}
                  checked={!!contract.billingStopped}
                  onChange={(e) =>
                    editContract(contractIndex, { billingStopped: e.target.checked })
                  }
                />
              </div>
            </details>
            <InstallmentPlanForm
              scenario={scenario}
              contractIndex={contractIndex}
              busy={busy}
              onChange={onChange}
            />
            <h4 className="text-[1.2rem] font-semibold text-black">支払いスケジュール</h4>
            <ul className="flex flex-col">
              {contract.installments.map((row, rowIndex) => {
                const label = `契約${contractIndex + 1} 分割${rowIndex + 1}`
                return (
                  <RecordRow
                    key={rowIndex}
                    title={label}
                    busy={busy}
                    onRemove={() =>
                      setInstallments(contractIndex, removeAt(contract.installments, rowIndex))
                    }
                    // 回は行の見出しに添える（欄に並べると5つ目だけが2段目に1つ落ちる）
                    aside={
                      <output aria-label={`回 ${label}`} className="text-caption text-text-muted">
                        {row.numberOfPayment == null ? "回は未設定" : `第${row.numberOfPayment}回`}
                      </output>
                    }
                  >
                    <div className={FIELD_GRID}>
                      <FormField label="約定日">
                        <DateInput
                          {...named("約定日", label)}
                          value={row.scheduledOn}
                          onChange={(e) =>
                            editInstallment(contractIndex, rowIndex, {
                              scheduledOn: e.target.value,
                            })
                          }
                        />
                      </FormField>
                      <FormField label="金額">
                        <TextField
                          {...named("金額", label)}
                          type="number"
                          min={0}
                          value={row.paymentTotalAmount}
                          onChange={(e) =>
                            editInstallment(contractIndex, rowIndex, {
                              paymentTotalAmount: Number(e.target.value),
                            })
                          }
                        />
                      </FormField>
                      <FormField label="入金済">
                        <TextField
                          {...named("入金済", label)}
                          type="number"
                          min={0}
                          value={row.paidAmount}
                          onChange={(e) =>
                            editInstallment(contractIndex, rowIndex, {
                              paidAmount: Number(e.target.value),
                            })
                          }
                        />
                      </FormField>
                      <ReadOnlyField label="入金日" ariaLabel={`入金日 ${label}`}>
                        {formatLocalDateTime(row.lastPaidAt)}
                      </ReadOnlyField>
                    </div>
                    <details
                      aria-label={`詳しい設定 ${label}`}
                      className="@container rounded-md border border-border-default px-3 py-2"
                    >
                      <summary className="cursor-pointer text-caption text-text-default">
                        詳しい設定（回・入金日・振替日程）／{transferSummary(row)}
                      </summary>
                      <div className={`${FIELD_GRID} pt-3`}>
                        <FormField label="回を変える">
                          <TextField
                            aria-label={`回を変える ${label}`}
                            type="number"
                            min={1}
                            max={1000}
                            value={row.numberOfPayment ?? ""}
                            onChange={(e) =>
                              editInstallment(contractIndex, rowIndex, {
                                numberOfPayment: optionalNumber(e.target.value),
                              })
                            }
                          />
                        </FormField>
                        <FormField
                          label="入金日を変える（任意）"
                          hint="入れると AI 入力の「過去の入金」に出ます。"
                        >
                          <TextField
                            aria-label={`入金日を変える ${label}（任意）`}
                            type="datetime-local"
                            value={toLocalInput(row.lastPaidAt)}
                            onChange={(e) =>
                              editInstallment(contractIndex, rowIndex, {
                                lastPaidAt: fromLocalInput(e.target.value) || null,
                              })
                            }
                          />
                        </FormField>
                      </div>
                      <div className={`${FIELD_GRID} pt-3`}>
                        <FormField label="振替日（任意）">
                          <DateInput
                            aria-label={`振替日 ${label}（任意）`}
                            value={row.transferOn ?? ""}
                            onChange={(e) =>
                              editInstallment(contractIndex, rowIndex, {
                                transferOn: e.target.value || null,
                              })
                            }
                          />
                        </FormField>
                        <FormField label="結果日（任意）">
                          <DateInput
                            aria-label={`結果日 ${label}（任意）`}
                            value={row.resultOn ?? ""}
                            onChange={(e) =>
                              editInstallment(contractIndex, rowIndex, {
                                resultOn: e.target.value || null,
                              })
                            }
                          />
                        </FormField>
                        {/* チェックは行全体に広げ、ラベルを折り返さない（狭い枠だと1文字ずつ縦に折れる） */}
                        <div className="col-span-full">
                          <Checkbox
                            label="振替請求あり"
                            className="whitespace-nowrap"
                            {...named("振替請求あり", label)}
                            checked={!!row.transferRequested}
                            onChange={(e) =>
                              editInstallment(contractIndex, rowIndex, {
                                transferRequested: e.target.checked,
                              })
                            }
                          />
                        </div>
                      </div>
                    </details>
                  </RecordRow>
                )
              })}
            </ul>
            <div>
              <Button
                variant="outline"
                disabled={busy || contract.installments.length >= RECORD_LIMITS.installments}
                onClick={() =>
                  setInstallments(contractIndex, [
                    ...contract.installments,
                    emptyInstallment(scenario, contract.installments),
                  ])
                }
              >
                分割を追加
              </Button>
            </div>
          </RecordRow>
        ))}
      </ul>
      <div className="pt-3">
        <Button
          variant="outline"
          disabled={busy || contracts.length >= RECORD_LIMITS.contracts}
          onClick={() =>
            onChange({
              contracts: [
                ...contracts,
                {
                  propertyName: null,
                  numberOfPayment: null,
                  totalPaymentAmount: null,
                  remainingPaymentCount: 1,
                  remainingBalance: 30_000,
                  installments: [emptyInstallment(scenario, [])],
                },
              ],
            })
          }
        >
          契約を追加
        </Button>
      </div>
    </Panel>
  )
}
